import React from 'react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../../ui/card';
import { Label } from '../../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Loader2 } from 'lucide-react';
import { ImageUploader } from '../../../ui/image-uploader';
import { useAuth } from '../../../../lib/auth/AuthContext';
import { Id } from '../../../../convex/_generated/dataModel';
import { GalleryContent, FileId } from '../../../../types/pageBuilder';
import { DEFAULT_GALLERY_CONTENT } from '../../../../constants/pageBuilderDefaults';
import { toast } from '../../../../lib/toast-utils';

interface GallerySectionProps {
  projectId: string;
  selectedLanguage: string;
  galleryComponent: any;
  galleryContent: GalleryContent;
  setGalleryContent: React.Dispatch<React.SetStateAction<GalleryContent>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  refreshComponents: () => void;
}

export const GallerySection: React.FC<GallerySectionProps> = ({
  projectId,
  selectedLanguage,
  galleryComponent,
  galleryContent,
  setGalleryContent,
  isLoading,
  setIsLoading,
  refreshComponents
}) => {
  const { useAuthenticatedMutation } = useAuth();
  const saveComponent = useAuthenticatedMutation('pageComponents:upsert');

  const handleSaveGalleryComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID or language is missing',
        variant: 'destructive',
      });
      return;
    }

    console.log('Saving gallery component with data:', galleryContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'projectGallery',
        order: 400, // Fourth component
        content: galleryContent,
        isActive: true,
        componentId: galleryComponent?._id
      });
      
      console.log('Gallery component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Gallery section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving gallery component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save gallery'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addGalleryImage = () => {
    setGalleryContent(prev => ({
      ...prev,
      images: [
        ...prev.images,
        {
          url: '',
          alt: '',
          type: 'horizontal',
          category: 'interior'
        }
      ]
    }));
  };

  const removeGalleryImage = (index: number) => {
    setGalleryContent(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const updateGalleryImage = (index: number, field: keyof GalleryContent['images'][0], value: string) => {
    setGalleryContent(prev => ({
      ...prev,
      images: prev.images.map((image, i) => 
        i === index ? { ...image, [field]: value } : image
      )
    }));
  };

  const handleGalleryImageUpload = (index: number, fileId: FileId | null, url: string) => {
    setGalleryContent(prev => ({
      ...prev,
      images: prev.images.map((image, i) => 
        i === index ? { ...image, url, imageId: fileId || undefined } : image
      )
    }));
  };

  const resetToDefault = () => {
    setGalleryContent(DEFAULT_GALLERY_CONTENT);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Project Gallery</CardTitle>
        <CardDescription>
          Add images to the project gallery
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button 
          variant="outline" 
          onClick={addGalleryImage}
          className="mb-4"
        >
          Add Gallery Image
        </Button>
        
        <div className="space-y-6">
          {galleryContent.images.map((image, index) => (
            <div key={index} className="border rounded-md p-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-medium">Image {index + 1}</h4>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeGalleryImage(index)}
                  className="h-8 text-red-500 hover:text-red-700"
                >
                  Remove
                </Button>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor={`image-url-${index}`}>Image</Label>
                  {projectId && (
                    <ImageUploader
                      projectId={projectId as Id<"projects">}
                      componentId={galleryComponent?._id}
                      purpose={`gallery-image-${index}`}
                      altText={image.alt || `Gallery image ${index + 1}`}
                      existingImageUrl={image.url}
                      existingFileId={image.imageId}
                      onUploadComplete={(fileId, url) => handleGalleryImageUpload(index, fileId, url)}
                      maxSizeMB={10}
                      acceptedTypes={['image/jpeg', 'image/png', 'image/webp']}
                      enableCompression={true}
                      compressionThresholdKB={200}
                      showHelper={index === 0}
                    />
                  )}
                </div>
                
                <div>
                  <Label htmlFor={`image-alt-${index}`}>Image Alt Text</Label>
                  <Input
                    id={`image-alt-${index}`}
                    value={image.alt}
                    onChange={(e) => updateGalleryImage(index, 'alt', e.target.value)}
                    placeholder="Enter image alt text"
                  />
                </div>
                
                <div>
                  <Label htmlFor={`image-type-${index}`}>Image Type</Label>
                  <Select 
                    value={image.type} 
                    onValueChange={(value) => updateGalleryImage(index, 'type', value as "horizontal" | "vertical" | "feature")}
                  >
                    <SelectTrigger id={`image-type-${index}`}>
                      <SelectValue placeholder="Select image type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="horizontal">Horizontal</SelectItem>
                      <SelectItem value="vertical">Vertical</SelectItem>
                      <SelectItem value="feature">Feature</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor={`image-category-${index}`}>Image Category</Label>
                  <Select 
                    value={image.category} 
                    onValueChange={(value) => updateGalleryImage(index, 'category', value as "interior" | "exterior" | "amenities")}
                  >
                    <SelectTrigger id={`image-category-${index}`}>
                      <SelectValue placeholder="Select image category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="interior">Interior</SelectItem>
                      <SelectItem value="exterior">Exterior</SelectItem>
                      <SelectItem value="amenities">Amenities</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
          
          {galleryContent.images.length === 0 && (
            <div className="text-center p-4 border border-dashed rounded-md">
              <p className="text-sm text-gray-500">No images added yet. Click the button above to add one.</p>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          onClick={resetToDefault}
          disabled={isLoading}
        >
          Reset
        </Button>
        <Button 
          onClick={handleSaveGalleryComponent}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : 'Save Gallery'}
        </Button>
      </CardFooter>
    </Card>
  );
}; 